import React from "react";
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  BooleanField,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  BooleanInput,
  ArrayInput,
  SimpleFormIterator,
  required,
  Show,
  SimpleShowLayout,
  DateField,
  ReferenceField,
  ReferenceInput,
  SearchInput,
  SelectInput,
  FilterButton,
  CreateButton,
  TopToolbar,
} from "react-admin";
import StringArrayField from "../components/StringArrayField";
import { SEVERITY_CHOICES } from "../consts";
import ExportAllButton from "../components/ExportAllButton";
import DynamicDropdownFilter from "../components/DynamicDropdownFilter";

const codeCveFilters = [
  <SearchInput
    source="q_name_description_vulnerableDependencyName_vulnerableDependencyPackageName"
    alwaysOn
  />,
  <DynamicDropdownFilter label="Name" source="name" resource="cve/code-cves" />,
  <DynamicDropdownFilter
    label="Code Repository"
    source="codeRepository"
    resource="cve/code-cves"
  />,
  <BooleanInput label="Fixable" source="fixable" />,
  <BooleanInput label="Reachable" source="reachable" />,
  <SelectInput source="severity" choices={SEVERITY_CHOICES} />,
];

const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <CreateButton />
    <ExportAllButton />
  </TopToolbar>
);

export const CodeCveList = (props: any) => (
  <List
    {...props}
    filters={codeCveFilters}
    actions={<ListActions />}
    sort={{ field: "cvssScore", order: "DESC" }}
  >
    <Datagrid rowClick="show">
      <TextField source="name" />
      <TextField source="codeRepository" />
      <NumberField source="cvssScore" />
      <NumberField source="epssScore" />
      <TextField source="severity" />
      <BooleanField source="fixable" />
      <BooleanField source="reachable" />
      <TextField source="vulnerableDependencyName" />
      <DateField source="createdAt" />
      <DateField source="updatedAt" />
    </Datagrid>
  </List>
);

export const CodeCveShow = (props: any) => (
  <Show {...props}>
    <SimpleShowLayout>
      <TextField source="name" />
      <TextField source="description" sx={{ whiteSpace: 'pre-wrap' }} />
      <ReferenceField source="codeSource" reference="cve/code-sources">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField
        source="codeSource"
        reference="cve/code-sources"
        link={false}
        label="Code Repository"
      >
        <TextField source="codeRepository" />
      </ReferenceField>
      <ReferenceField
        source="codeSource"
        reference="cve/code-sources"
        link={false}
        label="Endor Project UUID"
      >
        <TextField source="endorProjectUuid" />
      </ReferenceField>
      <ReferenceField
        source="codeSource"
        reference="cve/code-sources"
        link={false}
        label="Github Project ID"
      >
        <TextField source="githubProjectId" />
      </ReferenceField>
      <NumberField source="cvssScore" />
      <NumberField source="epssScore" />
      <TextField source="severity" />
      <TextField source="uuid" />
      <StringArrayField source="tags" />
      <BooleanField source="fixable" />
      <BooleanField source="reachable" />
      <TextField source="vulnerableDependencyName" />
      <TextField source="vulnerableDependencyPackageName" />
      <TextField source="vulnerableDependencyVersion" />
      <StringArrayField source="vulnerableDependencyFixVersions" />
      <StringArrayField source="vulnerableDependencyPaths" />
      <DateField source="createdAt" />
      <DateField source="updatedAt" />
    </SimpleShowLayout>
  </Show>
);

export const CodeCveEdit = (props: any) => (
  <Edit {...props} redirect="show">
    <SimpleForm>
      <TextInput source="name" validate={required()} />
      <TextInput
        source="description"
        validate={required()}
        multiline
        rows={5}
      />
      <TextInput disabled source="codeRepository" />
      <NumberInput
        source="cvssScore"
        validate={required()}
        min={0}
        max={10}
        step={0.1}
      />
      <NumberInput
        source="epssScore"
        validate={required()}
        min={0}
        max={1}
        step={0.00001}
      />
      <SelectInput
        source="severity"
        choices={SEVERITY_CHOICES}
        validate={required()}
      />
      <TextInput source="uuid" validate={required()} disabled />
      <ArrayInput source="tags">
        <SimpleFormIterator>
          <TextInput source="" />
        </SimpleFormIterator>
      </ArrayInput>
      <BooleanInput source="fixable" />
      <BooleanInput source="reachable" />
      <TextInput source="vulnerableDependencyName" validate={required()} />
      <TextInput
        source="vulnerableDependencyPackageName"
        validate={required()}
      />
      <TextInput source="vulnerableDependencyVersion" validate={required()} />
      <ArrayInput source="vulnerableDependencyFixVersions">
        <SimpleFormIterator>
          <TextInput source="" />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source="vulnerableDependencyPaths">
        <SimpleFormIterator>
          <TextInput source="" />
        </SimpleFormIterator>
      </ArrayInput>
    </SimpleForm>
  </Edit>
);

export const CodeCveCreate = (props: any) => (
  <Create {...props} redirect={"show"}>
    <SimpleForm>
      <TextInput source="name" validate={required()} />
      <TextInput
        source="description"
        validate={required()}
        multiline
        rows={5}
      />
      <ReferenceInput source="codeSource" reference="cve/code-sources" />
      <NumberInput
        source="cvssScore"
        validate={required()}
        min={0}
        max={10}
        step={0.1}
      />
      <NumberInput
        source="epssScore"
        validate={required()}
        min={0}
        max={1}
        step={0.00001}
      />
      <TextInput source="uuid" validate={required()} />
      <ArrayInput source="tags">
        <SimpleFormIterator>
          <TextInput source="" />
        </SimpleFormIterator>
      </ArrayInput>
      <BooleanInput source="fixable" />
      <BooleanInput source="reachable" />
      <TextInput source="vulnerableDependencyName" validate={required()} />
      <TextInput
        source="vulnerableDependencyPackageName"
        validate={required()}
      />
      <TextInput source="vulnerableDependencyVersion" validate={required()} />
      <ArrayInput source="vulnerableDependencyFixVersions">
        <SimpleFormIterator>
          <TextInput source="" />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source="vulnerableDependencyPaths">
        <SimpleFormIterator>
          <TextInput source="" />
        </SimpleFormIterator>
      </ArrayInput>
    </SimpleForm>
  </Create>
);
